import { Component, h, Prop, Element, Host } from '@stencil/core';
import { getClassListString, stripHtmlFromString } from '../component-utils/stencilUtils';

const CLASSES = {
  DESCRIPTION: '.adl-cme-summary-card__content-description',
  FADE_OUT: 'adl-cme-summary-card__content-description--fadeout',
};
/**
 * COMPNAME is a component used to display the summary of CME. The card will have a summary of the CME and a clickable button to link to the CME.
 *
 * <em>For More Information:</em>
 * AEM Component- N/A
 * Sling Model- core/src/main/java/org/aafp/aem/core/models/AafpTagListCard.java
 */
@Component({
  tag: 'aafp-cme-summary-card',
  shadow: false,
})
export class AafpCmeSummaryCard {
  @Element() element: HTMLElement;
  /**
   * For creating the Url of image asset
   */
  @Prop() imgSrc: string;
  /**
   * For labeling the card as "featured" or "new" etc
   */
  @Prop() featuredTitle: string;
  /**
   * For creating the card's title
   */
  @Prop() cardTitle: string;
  /**
   * For creating the card's text
   */
  @Prop() description: string;
  /**
   * For creating the amount of credits to display
   */
  @Prop() credits: string;
  /**
   * For creating the text of the CME format. E.g. "self-study"
   */
  @Prop() format: string;
  /**
   * For storing the CME card's date asset
   */
  @Prop() date: string;
  /**
   * For storing the CME card's location asset
   */
  @Prop() location: string;
  /**
   * For storing the link to the CME page
   */
  @Prop() linkUrl: string;
  /**
   * For setting the CME to members only
   */
  @Prop({ mutable: true }) isRestricted: boolean = false;
  /**
   * For setting the card kicker
   */
  @Prop() cardKicker?: string;
  /**
   * For setting the card deck
   */
  @Prop() cardDeck?: string;
  /**
   * For setting the card author
   */
  @Prop() cardAuthor?: string;
  /**
   * For displaying a secondary button
   */
  @Prop({ mutable: true }) secondaryButton?: boolean = false;
  /**
   * For displaying secondary button text. Also used to determine if the secondary button displays.
   */
  @Prop({ mutable: true }) secondaryBtnMessage?: string = '';

  componentDidLoad() {
    this.applyFadeoutIfOverflow(this.element);
  }

  applyFadeoutIfOverflow(hostElRef: HTMLElement) {
    const descriptionElRef = hostElRef.querySelector(CLASSES.DESCRIPTION);

    if (!descriptionElRef) return;

    if (descriptionElRef.scrollHeight > descriptionElRef.clientHeight) {
      descriptionElRef.classList.add(CLASSES.FADE_OUT);
    }
  }

  render() {
    return (
      <Host class="adl-cme-summary-card__host">
        <div class="adl-cme-summary-card__image">
          <img class="adl-cme-summary-card__image-asset" src={this.imgSrc} />
          <p
            class={getClassListString(
              { 'adl-cme-summary-card__image-featured--hide': !this.featuredTitle },
              'adl-body adl-font__regular adl-cme-summary-card__image-featured',
            )}
          >
            {this.featuredTitle}
          </p>
        </div>
        <div class="adl-cme-summary-card__content">
          <p class="adl-body adl-text-align--left">{this.cardKicker}</p>
          <p
            class="adl-h2 adl-h2--mobile-only adl-font__light adl-cme-summary-card__content-title"
            innerHTML={this.cardTitle}
          ></p>
          <p class="adl-body adl-text-align--left">{this.cardDeck}</p>
          <p class="adl-body adl-font__regular adl-cme-summary-card__content-description">{this.description}</p>
          {this.cardAuthor ? <p class="adl-body adl-cme-summary-card__content-cardAuthor">- {this.cardAuthor}</p> : ''}
          <p
            class={getClassListString(
              { 'adl-cme-summary-card__content-details--hide': !this.isRestricted },
              'adl-body adl-cme-summary-card__content-details',
            )}
          >
            <i class="material-icons adl-cme-summary-card__content-details-icon adl-cme-summary-card__content-details-icon--restricted">
              vpn_key
            </i>
            Available to Members Only
          </p>
          {this.credits ? (
            <p class="adl-body adl-cme-summary-card__content-details">
              <i class="material-icons adl-cme-summary-card__content-details-icon">assignment</i> {this.credits}
            </p>
          ) : (
            ''
          )}
          <p
            class={getClassListString(
              { 'adl-cme-summary-card__content-details--hide': !this.format },
              'adl-body adl-cme-summary-card__content-details',
            )}
          >
            <i class="material-icons adl-cme-summary-card__content-details-icon">laptop</i> {this.format}
          </p>
          <p
            class={getClassListString(
              { 'adl-cme-summary-card__content-details--hide': !this.date },
              'adl-body adl-cme-summary-card__content-details',
            )}
          >
            <i class="material-icons adl-cme-summary-card__content-details-icon md-24">event</i> {this.date}
          </p>
          <p
            class={getClassListString(
              { 'adl-cme-summary-card__content-details--hide': !this.location },
              'adl-body adl-cme-summary-card__content-details',
            )}
          >
            <i class="material-icons adl-cme-summary-card__content-details-icon">room</i> {this.location}
          </p>

          {this.secondaryBtnMessage ? (
            <aafp-cta-button
              class="adl-cme-summary-card__content-cta--secondaryButton"
              label={this.secondaryBtnMessage}
              is-active="true"
              is-secondary="true"
              href={this.linkUrl}
              aria-label={stripHtmlFromString(this.cardTitle)}
              slot="cta-button"
            ></aafp-cta-button>
          ) : (
            <aafp-cta-button
              class="adl-cme-summary-card__content-details--hide"
              label={this.secondaryBtnMessage}
              is-active="true"
              is-secondary="true"
              href={this.linkUrl}
              aria-label={stripHtmlFromString(this.cardTitle)}
              slot="cta-button"
            ></aafp-cta-button>
          )}

          <aafp-cta-button
            class="adl-cme-summary-card__content-cta"
            label="View"
            is-active="true"
            href={this.linkUrl}
            aria-label={stripHtmlFromString(this.cardTitle)}
            slot="cta-button"
          ></aafp-cta-button>
        </div>
      </Host>
    );
  }
}
